import React, { useState } from 'react';
import {
  Box,
  Popover,
  Chip,
  Typography,
  List,
  ListSubheader,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
} from '@mui/material';
import MedicationIcon from '@mui/icons-material/Medication';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../context/ProjectContext';
import { drugService } from '../../services/drugService';
import { projectService } from '../../services/projectService';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';
import { projectListPrimaryLabel, projectListSecondaryLabel } from '../../utils/projectIdentityFields';

/**
 * Study switcher — the Projects → Drugs → Studies picker that used to live in
 * the left sidebar (see Home.jsx). Surfaced from the top navbar's study chip
 * so a study can be switched from any page without going back to Home first.
 * Selecting a study mirrors Home.jsx's study-card click: loads it as the
 * current project and navigates into its workspace.
 */
const StudySwitcher = ({ chipSx, size = 'medium' }) => {
  const navigate = useNavigate();
  const { currentProject, loadProject } = useProject();

  const [anchorEl, setAnchorEl] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadingDrugs, setLoadingDrugs] = useState(false);
  const [drugs, setDrugs] = useState([]);
  const [studiesByDrug, setStudiesByDrug] = useState({});
  const [loadingStudies, setLoadingStudies] = useState({});
  const [search, setSearch] = useState('');

  const fetchStudiesForDrug = async (drugId) => {
    setLoadingStudies((prev) => ({ ...prev, [drugId]: true }));
    try {
      const response = await projectService.listProjects({ drug: drugId });
      setStudiesByDrug((prev) => ({ ...prev, [drugId]: response.data || [] }));
    } catch (err) {
      console.error('StudySwitcher: error loading studies for drug', drugId, err);
    }
    setLoadingStudies((prev) => ({ ...prev, [drugId]: false }));
  };

  const fetchDrugs = async () => {
    setLoadingDrugs(true);
    try {
      const list = await drugService.getAllDrugs();
      setDrugs(list);
      list.forEach((drug) => fetchStudiesForDrug(drug._id));
    } catch (err) {
      console.error('StudySwitcher: error loading projects', err);
    }
    setLoadingDrugs(false);
    setLoaded(true);
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    if (!loaded) fetchDrugs();
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const handleSelectStudy = async (study) => {
    await loadProject(study._id);
    handleClose();
    navigate(`/clinical-intake?project=${study._id}&tab=protocol`);
  };

  const term = search.trim().toLowerCase();
  const matchesSearch = (drug, study) =>
    !term ||
    drug.name?.toLowerCase().includes(term) ||
    projectListPrimaryLabel(study)?.toLowerCase().includes(term) ||
    projectListSecondaryLabel(study)?.toLowerCase().includes(term);

  const chipLabel = currentProject ? projectListPrimaryLabel(currentProject) : 'Select Study';

  return (
    <>
      <Chip
        label={chipLabel}
        size={size}
        onClick={handleOpen}
        clickable
        variant="outlined"
        sx={chipSx}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{ sx: { mt: 1, width: 360, maxHeight: '70vh' } }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search projects or studies…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ overflowY: 'auto', maxHeight: 'calc(70vh - 64px)' }}>
          {loadingDrugs ? (
            <LoadingState compact label="Loading projects…" />
          ) : drugs.length === 0 ? (
            <EmptyState compact title="No projects yet" description="Projects you're added to will show up here." />
          ) : (
            <List dense disablePadding>
              {drugs.map((drug) => {
                const studies = (studiesByDrug[drug._id] || []).filter((study) => matchesSearch(drug, study));
                if (term && studies.length === 0) return null;
                return (
                  <li key={drug._id}>
                    <ul style={{ padding: 0 }}>
                      <ListSubheader sx={{ display: 'flex', alignItems: 'center', gap: 1, lineHeight: '36px' }}>
                        <MedicationIcon fontSize="small" sx={{ color: '#3f9e5e' }} />
                        {drug.name}
                      </ListSubheader>
                      {loadingStudies[drug._id] ? (
                        <LoadingState compact label="Loading studies…" />
                      ) : studies.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 1 }}>
                          No studies yet
                        </Typography>
                      ) : (
                        studies.map((study) => {
                          const isSelected = currentProject && currentProject._id === study._id;
                          const secondary = projectListSecondaryLabel(study);
                          return (
                            <ListItemButton
                              key={study._id}
                              selected={isSelected}
                              onClick={() => handleSelectStudy(study)}
                              sx={{ pl: 4 }}
                            >
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <LocalHospitalIcon fontSize="small" sx={{ color: isSelected ? 'primary.main' : 'text.secondary' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={projectListPrimaryLabel(study)}
                                secondary={secondary || undefined}
                                primaryTypographyProps={{ fontWeight: isSelected ? 700 : 500, fontSize: '0.875rem' }}
                              />
                            </ListItemButton>
                          );
                        })
                      )}
                    </ul>
                  </li>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default StudySwitcher;
